import type { OrganizationId } from './identity';
import { getCommittedOrganization, getOrganizationGeneration, getOrganizationSignal } from './organization';
import { getAuthGeneration, getSessionAuthSignal, getSessionSubject } from './token';

// Rejection envelope for work terminated by a session or organization
// transition (logout, account switch, organization switch). It is control
// flow, not a failure: global handlers and callers must stay quiet on it.
// code/__CANCEL__ make axios.isCancel() treat it as a cancellation.
export class SessionTransitionError extends Error {
  readonly code = 'ERR_CANCELED';
  readonly __CANCEL__ = true;

  constructor(message = 'Operation superseded because the active session changed') {
    super(message);
    this.name = 'SessionTransitionError';
  }
}

export const isSessionTransition = (error: unknown): error is SessionTransitionError => error instanceof SessionTransitionError;

// Operation liveness. Two predicates, not one, because the lifecycle is
// two-phase (docs/organization-identity/lifecycle-policies.md):
//
// - isScopeCurrent gates publication (UI state, cache writes, toasts,
//   navigation, downloads) and pre-durable dispatch. It requires the
//   organization, subject, and auth generation to all be unchanged.
// - isActorCurrent gates post-durable continuation and compensation: a
//   workflow whose first write may already be applied server-side may finish
//   under its origin organization after the route moved on, but never across
//   a subject or auth-generation change.
//
// The React remount boundaries (session boundary in KeycloakSessionBridge,
// organization boundary in OrganizationScope) tear down mounted UI but do not
// interrupt in-flight async handlers, confirmation chains, polling, or 401
// replays - that is what these predicates and signals are for.
export type OperationContext = Readonly<{
  organization: OrganizationId;
  subject: string | null;
  authGeneration: number;
  scopeSignal: AbortSignal;
  actorSignal: AbortSignal;
  isScopeCurrent(): boolean;
  isActorCurrent(): boolean;
}>;

// The transport requirement for one request inside a time-spanning
// operation. The phases deliberately describe lifecycle position rather than
// HTTP method:
//
// - scope: preparation/read work; scope-current before and after transport
// - start: the first durable write; scope-current before transport entry,
//   actor-current after it
// - continue: a later request/compensation after a durable write;
//   actor-current throughout
//
// Callers still own P5 publication checks. In particular, a `start` or
// `continue` response may reach the workflow after an organization switch so
// it can finish or compensate, but it must not toast, navigate, write a cache,
// or publish a download unless `context.isScopeCurrent()` is true.
export type OperationPhase = 'scope' | 'start' | 'continue';

export type OperationRequirement = Readonly<{
  context: OperationContext;
  phase: OperationPhase;
  // Axios deep-merges custom config objects before interceptors run. Closure
  // methods preserve one marker across that clone without exposing mutable
  // state as request data.
  didEnterTransport(): boolean;
  markTransportEntered(): void;
}>;

export const operationRequirement = (context: OperationContext, phase: OperationPhase): OperationRequirement => {
  let enteredTransport = false;
  return Object.freeze({
    context,
    phase,
    didEnterTransport: () => enteredTransport,
    markTransportEntered: () => {
      enteredTransport = true;
    },
  });
};

export const markOperationRequirementTransportEntered = (requirement: OperationRequirement): void => {
  requirement.markTransportEntered();
};

export const didOperationRequirementEnterTransport = (requirement: OperationRequirement): boolean => requirement.didEnterTransport();

export const isOperationRequirementCurrent = (
  requirement: OperationRequirement,
  { transportEntered, semanticRead }: { transportEntered: boolean; semanticRead: boolean }
): boolean => {
  // Reads never gain uncertain-write semantics. Treating a mistakenly marked
  // `start` GET as actor-only after transport would let stale tenant data
  // resolve into a later continuation.
  if (requirement.phase === 'scope' || (requirement.phase === 'start' && (!transportEntered || semanticRead))) {
    return requirement.context.isScopeCurrent();
  }
  return requirement.context.isActorCurrent();
};

export const operationRequirementSignal = (requirement: OperationRequirement): AbortSignal =>
  requirement.phase === 'continue' ? requirement.context.actorSignal : requirement.context.scopeSignal;

export const acquireOperationContext = (organization: OrganizationId): OperationContext => {
  const subject = getSessionSubject();
  const authGeneration = getAuthGeneration();
  const organizationGeneration = getOrganizationGeneration();
  const actorSignal = getSessionAuthSignal();
  const scopeSignal = AbortSignal.any([getOrganizationSignal(), actorSignal]);

  const isActorCurrent = (): boolean => !actorSignal.aborted && subject === getSessionSubject() && authGeneration === getAuthGeneration();

  return Object.freeze({
    organization,
    subject,
    authGeneration,
    scopeSignal,
    actorSignal,
    isActorCurrent,
    isScopeCurrent: () =>
      !scopeSignal.aborted &&
      isActorCurrent() &&
      organizationGeneration === getOrganizationGeneration() &&
      organization === getCommittedOrganization(),
  });
};
